import { animationSpeedOptions, settings } from '~/modals/Settings.svelte';
import type { SpringOptions } from './springs';
import { tick } from 'svelte';

class AnimationSpeed {
  speed = $derived<number>(settings.animationSpeed.current ?? 3);
  name = $derived(animationSpeedOptions[this.speed].name);
  spring = $derived(animationSpeedOptions[this.speed].spring);
  ms = $derived(animationSpeedOptions[this.speed].ms);
}

export const animationSpeed = new AnimationSpeed();

const numberPattern = new RegExp(
  /translate3d\((-?\d+(?:\.\d+)?)px, (-?\d+(?:\.\d+)?)px/,
);
const translatePattern = /^(-?\d+(?:\.\d+)?)px(?: (-?\d+(?:\.\d+)?)px)?/;

// A tile's position: `transform: translate3d(...)`, or the `translate` property that neodrag v3
// writes for draggable tiles. Animations that write an absolute `transform` from this position also
// set `translate: none` so the two aren't added together while they run.
export const getTranslate = (node: HTMLElement) => {
  const [translate_x, translate_y] = node.style.transform
    .match(numberPattern)
    ?.slice(1)
    ?.map((p) => +p) ??
    // browsers serialize `translate: 5px 0px` as `5px`
    node.style.translate.match(translatePattern)?.slice(1)?.map((p) => +(p ?? 0)) ?? [0, 0];

  return [translate_x, translate_y];
};

export const getDuration = (d: number) => {
  return animationSpeed.ms + 50 * Math.sqrt(Math.abs(d));
};

export class Animation {
  animations = $state<Promise<unknown>[]>([]);
  buffer: number;

  constructor(buffer?: number) {
    this.buffer = buffer || 80;
  }

  queue(dur: number) {
    this.animations.push(delay(dur + this.buffer));
  }

  async settled(fn?: () => Promise<void>) {
    await tick();
    await Promise.all(
      this.animations.map(async (a) => {
        await a;
        await fn?.();
      }),
    );
    this.animations = [];
  }
}

export const SPRING: SpringOptions = {
  stiffness: 0.2,
  damping: 1,
  precision: 1,
  fps: 60,
};

export const delay = <T extends unknown>(t: number, value?: T) => {
  let res: (val: unknown) => void;
  let rej: (val: unknown) => void;
  const p = new Promise(function (resolve, reject) {
    res = resolve;
    rej = reject;
  });
  p._timeout = setTimeout(() => {
    res(value);
  }, t);
  p.cancel = (err?: Error) => {
    // rej(err ?? new Error("Timeout"));
    clearTimeout(p._timeout);
  };
  return p;
};
